/**
 * DEVLOGS-TEMP — run identity, sequence numbers and the monotonic clock.
 *
 * No crypto dependency: `crypto.randomUUID` is probed and a small PRNG-free
 * fallback is used where it is absent (older Hermes). Ids only have to be
 * unique enough to separate concurrent runs in a dev fleet, not unguessable.
 */

const ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";

function randomHex(bytes: number): string {
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } };
  const array = new Uint8Array(bytes);
  try {
    if (typeof g.crypto?.getRandomValues === "function") {
      g.crypto.getRandomValues(array);
    } else {
      for (let i = 0; i < bytes; i += 1) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
  } catch {
    for (let i = 0; i < bytes; i += 1) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  let out = "";
  for (let i = 0; i < bytes; i += 1) {
    out += ALPHABET[(array[i] ?? 0) % ALPHABET.length];
  }
  return out;
}

/** Time-prefixed so runs sort chronologically in a plain string sort. */
export function newRunId(now = Date.now()): string {
  return `r_${now.toString(36)}${randomHex(8)}`;
}

export function newSessionId(): string {
  return `s_${randomHex(12)}`;
}

export function newSpanId(): string {
  return randomHex(8);
}

export function newTraceId(): string {
  return `t_${randomHex(12)}`;
}

/**
 * Strictly increasing within a run. Assigned at record CREATION, which is what
 * makes a gap in the sequence mean "a record was dropped" rather than "a batch
 * arrived out of order".
 */
export class SeqCounter {
  #next = 1;

  take(): number {
    const value = this.#next;
    this.#next = value >= 0xffffffff ? 1 : value + 1;
    return value;
  }

  peek(): number {
    return this.#next;
  }
}

export interface MonotonicClock {
  /** Wall clock, epoch ms. */
  now(): number;
  /** ms since the clock was created — immune to NTP steps and wrong clocks. */
  mono(): number;
}

export function createMonotonicClock(
  now: () => number = Date.now,
  perf?: { now(): number } | null,
): MonotonicClock {
  const perfSource =
    perf ??
    (globalThis as { performance?: { now(): number } }).performance ??
    null;
  const startWall = safeNow(now);
  const startPerf = perfSource ? safeCall(() => perfSource.now(), 0) : null;
  return {
    now: () => safeNow(now),
    mono: () => {
      if (perfSource && startPerf !== null) {
        const value = safeCall(() => perfSource.now(), 0) - startPerf;
        return value >= 0 ? Math.round(value) : 0;
      }
      const value = safeNow(now) - startWall;
      return value >= 0 ? value : 0;
    },
  };
}

function safeNow(now: () => number): number {
  const value = safeCall(now, 0);
  return Number.isFinite(value) ? value : 0;
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
